<template>
  <div class="password-field">
    <div class="input-row">
      <Password
        :modelValue="modelValue"
        @update:modelValue="$emit('update:modelValue', $event)"
        :placeholder="placeholder"
        :feedback="false"
        toggleMask
        class="flex-1"
        :inputClass="inputClass"
      />
      <Button
        icon="pi pi-refresh"
        v-tooltip="'Generar contraseña segura'"
        severity="secondary"
        outlined
        @click="generate"
        type="button"
      />
      <Button
        v-if="modelValue"
        icon="pi pi-copy"
        v-tooltip="copied ? '¡Copiado!' : 'Copiar contraseña'"
        :severity="copied ? 'success' : 'secondary'"
        outlined
        @click="copy"
        type="button"
      />
    </div>

    <!-- Barra de fortaleza -->
    <div v-if="modelValue" class="strength-bar">
      <div class="strength-track">
        <div
          class="strength-fill"
          :style="{ width: (strength.score / 6 * 100) + '%', background: strength.color }"
        />
      </div>
      <span class="strength-label" :style="{ color: strength.color }">
        {{ strength.label }}
      </span>
    </div>

    <!-- Contraseña generada visible -->
    <div v-if="showGenerated && modelValue" class="generated-preview">
      <i class="pi pi-key" />
      <code>{{ modelValue }}</code>
      <span class="preview-hint">Guarda esta contraseña antes de continuar</span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import Password from 'primevue/password';
import Button from 'primevue/button';
import { generatePassword, evaluateStrength } from '../../utils/passwordGenerator';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'Contraseña' },
  inputClass: { type: String, default: '' },
  autoGenerate: { type: Boolean, default: false },  // generar al montar
});

const emit = defineEmits(['update:modelValue']);
const copied = ref(false);
const showGenerated = ref(false);

const strength = computed(() => evaluateStrength(props.modelValue));

const generate = () => {
  const pwd = generatePassword(14);
  emit('update:modelValue', pwd);
  showGenerated.value = true;
  // Ocultar después de 30 segundos por seguridad
  setTimeout(() => { showGenerated.value = false; }, 30000);
};

const copy = async () => {
  if (!props.modelValue) return;
  try {
    await navigator.clipboard.writeText(props.modelValue);
    copied.value = true;
    setTimeout(() => { copied.value = false; }, 2000);
  } catch (e) {
    console.error('No se pudo copiar:', e);
  }
};

// Auto-generar al montar si se solicita
if (props.autoGenerate && !props.modelValue) {
  generate();
}
</script>

<style scoped>
.password-field { display: flex; flex-direction: column; gap: 0.4rem; }

.input-row { display: flex; gap: 0.4rem; align-items: center; }
.flex-1 { flex: 1; }
.flex-1 :deep(input) { width: 100%; }

/* Barra de fortaleza */
.strength-bar { display: flex; align-items: center; gap: 0.5rem; }
.strength-track {
  flex: 1; height: 4px; background: #e5e7eb; border-radius: 2px; overflow: hidden;
}
.strength-fill { height: 100%; border-radius: 2px; transition: width 0.3s, background 0.3s; }
.strength-label { font-size: 0.72rem; font-weight: 600; min-width: 50px; }

/* Preview de contraseña generada */
.generated-preview {
  display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;
  padding: 0.5rem 0.75rem; background: #f0fdf4; border: 1px solid #bbf7d0;
  border-radius: 6px; font-size: 0.82rem;
}
.generated-preview .pi-key { color: #16a34a; }
.generated-preview code {
  font-family: monospace; font-size: 0.9rem; font-weight: 700;
  color: #15803d; letter-spacing: 0.05em;
}
.preview-hint { color: #6b7280; font-size: 0.75rem; }
</style>
